import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  Phone,
  MapPin,
  Clock,
  Hospital,
  Briefcase,
  Droplet,
  HeartPulse,
  Repeat,
  Users,
  Building2,
  AlertTriangle,
  Accessibility,
  Route,
} from 'lucide-react';

const CANONICAL_PATH = '/katy-tx-medical-transportation';

const KATY_FAQS = [
  {
    question: 'What Katy-area neighborhoods and master-planned communities does MoveraCare serve?',
    answer:
      'MoveraCare provides non-emergency medical transportation across the Katy area, including Cinco Ranch, Cross Creek Ranch, Firethorne, Grand Lakes, Kelliwood, Nottingham Country, Seven Meadows, Cane Island, Tamarron, Ventana Lakes, Falcon Ranch, and the areas along Grand Parkway (99) and I-10 west. Service is scheduled subject to availability.',
  },
  {
    question: 'Katy sits across three counties — do you cover all of them?',
    answer:
      'Yes. The Katy area spans Fort Bend, Harris, and Waller Counties. MoveraCare’s core service area is Fort Bend County, with confirmed coverage across Harris County. Trips reaching into far West Katy or Waller County can typically be accommodated with advance scheduling and, in some cases, extended-distance authorization.',
  },
  {
    question: 'Do you transport patients to hospitals along the Katy Freeway and Grand Parkway?',
    answer:
      'Yes. MoveraCare provides non-emergency wheelchair and ambulatory transportation for Katy-area patients traveling to appointments at hospitals, specialty clinics, and outpatient centers along the Katy Freeway (I-10) and Grand Parkway (99), subject to scheduling availability.',
  },
  {
    question: 'Do you offer recurring dialysis transportation in Katy?',
    answer:
      'Yes. Standing weekly schedules are supported for Katy-area dialysis patients — Monday/Wednesday/Friday, Tuesday/Thursday/Saturday, or other recurring plans coordinated with the treating dialysis center. Wait-and-return coordination can be arranged in advance.',
  },
  {
    question: 'Can Katy hospitals and skilled nursing facilities request discharge transportation?',
    answer:
      'Yes. Katy-area discharge planners, case managers, and social workers can request non-emergency discharge transportation from a Katy hospital or SNF to home, rehabilitation, or assisted living. Same-day and scheduled discharges are supported for medically stable patients.',
  },
  {
    question: 'How do I book a wheelchair-accessible ride in Katy?',
    answer:
      'Call dispatch at (832) 925-2128 or use the online Book a Ride form. For a family member or care coordinator arranging transportation on behalf of a Katy patient, the Request Patient Transportation form captures the details we need.',
  },
  {
    question: "Do you accept Workers' Compensation authorizations for Katy-area injured workers?",
    answer:
      "Yes. MoveraCare accepts authorized non-emergency transportation for Katy-area injured workers traveling to Workers' Compensation-approved medical, therapy, IME, FCE, and occupational medicine appointments. Authorization is determined by the applicable adjuster, case manager, TPA, or payer.",
  },
  {
    question: 'How far in advance should Katy riders schedule?',
    answer:
      'We recommend at least 24–48 hours notice for routine Katy trips, particularly for wheelchair transportation and appointments in far West Katy or Waller County. Recurring dialysis, therapy, and rehabilitation runs should be set up in advance as a standing schedule. Same-day requests may be considered when vehicle and driver availability allows.',
  },
];

const KATY_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'MedicalBusiness',
  '@id': 'https://www.moveracaremobility.com/katy-tx-medical-transportation#business',
  name: 'MoveraCare Mobility — Katy, TX Non-Emergency Medical Transportation',
  legalName: 'MoveraCare Services LLC',
  description:
    "Richmond, TX-based non-emergency medical transportation provider serving Katy and the surrounding Fort Bend, Harris, and Waller County areas — wheelchair and ambulatory transportation for medical appointments, dialysis, hospital discharge, rehabilitation, and Workers' Compensation.",
  url: 'https://www.moveracaremobility.com/katy-tx-medical-transportation',
  telephone: '+1-832-925-2128',
  email: 'dispatch@moveracaremobility.com',
  areaServed: [
    { '@type': 'City', name: 'Katy, TX' },
    { '@type': 'AdministrativeArea', name: 'Fort Bend County, Texas' },
    { '@type': 'AdministrativeArea', name: 'Harris County, Texas' },
    { '@type': 'AdministrativeArea', name: 'Waller County, Texas' },
  ],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Richmond',
    addressRegion: 'TX',
    addressCountry: 'US',
  },
  openingHoursSpecification: [
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'], opens: '06:00', closes: '19:00' },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Sunday'], opens: '12:00', closes: '17:00' },
  ],
};

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: KATY_FAQS.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
};

const SERVICE_TYPES = [
  { icon: Accessibility, title: 'Wheelchair Transportation', desc: 'Wheelchair-accessible transportation for Katy-area patients traveling to medical destinations.', to: '/services/wheelchair-transportation' },
  { icon: Users, title: 'Ambulatory Transportation', desc: 'Comfortable transport for medically stable ambulatory patients across Katy and West Houston.', to: '/services/ambulatory-transportation' },
  { icon: Hospital, title: 'Hospital Discharge Transportation', desc: 'Same-day and scheduled discharge transportation from Katy hospitals to home, SNF, rehab, or assisted living.', to: '/services/hospital-discharge' },
  { icon: Droplet, title: 'Dialysis Transportation', desc: 'Recurring standing schedules for Katy dialysis patients aligned to treatment days.', to: '/services/dialysis-transportation' },
  { icon: HeartPulse, title: 'Therapy & Rehabilitation Transportation', desc: 'Physical therapy, occupational therapy, and rehabilitation transportation for Katy-area patients.', to: '/services/therapy-rehabilitation-transportation' },
  { icon: Briefcase, title: "Workers' Compensation Transportation", desc: 'Authorized transportation for Katy-area injured workers to approved medical, therapy, and IME destinations.', to: '/healthcare-partners/workers-compensation' },
  { icon: Repeat, title: 'Recurring Medical Transportation', desc: 'Standing NEMT schedules for Katy patients with ongoing treatment plans.', to: '/services/recurring-medical-transportation' },
  { icon: Clock, title: 'Wait-and-Return Transportation', desc: 'Driver-waiting service for shorter Katy appointments when arranged in advance.', to: '/services' },
];

const KATY_AREAS = [
  'Cinco Ranch',
  'Cross Creek Ranch',
  'Firethorne',
  'Grand Lakes',
  'Kelliwood',
  'Nottingham Country',
  'Seven Meadows',
  'Cane Island',
  'Tamarron',
  'Ventana Lakes',
  'Falcon Ranch',
  'Willow Fork',
  'Green Trails',
  'Pine Mill Ranch',
  'Katy Old Town',
  'Fulshear (adjacent)',
  'Brookshire (adjacent)',
  'Waller (adjacent)',
];

const NEIGHBORHOOD_ANCHORS = [
  { name: 'Katy Freeway & Grand Parkway medical corridor', desc: 'Katy patients frequently travel to hospitals, specialty clinics, and outpatient centers along the I-10 Katy Freeway and Grand Parkway (99) corridor. MoveraCare coordinates non-emergency wheelchair and ambulatory transportation to appointments along these corridors, subject to scheduling availability.' },
  { name: 'Katy-area dialysis centers', desc: 'Recurring weekly transportation for Katy dialysis patients on standing 3-day-per-week schedules coordinated with the treating dialysis center — including patients living in master-planned communities south and north of I-10.' },
  { name: 'Katy hospital & SNF discharge coordination', desc: 'Same-day and scheduled discharge transportation from Katy-area hospitals and skilled nursing facilities to home, rehabilitation, or assisted living for medically stable patients.' },
  { name: 'Occupational health & Workers’ Comp destinations', desc: 'Non-emergency transportation for authorized injured worker appointments across Katy, including physical therapy, IME, FCE, and occupational medicine visits.' },
];

const KatyMedicalTransportation = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="katy-page">
      <Seo
        title="Medical Transportation Katy TX | Wheelchair &amp; Ambulatory NEMT | MoveraCare"
        description="Medical transportation Katy TX. MoveraCare Mobility (based in Richmond, TX) provides professional wheelchair and ambulatory non-emergency medical transportation serving Katy, Fort Bend County, Harris County, Waller County, and Greater Houston. Call 832-925-2128."
        path={CANONICAL_PATH}
        schema={[KATY_SCHEMA, FAQ_SCHEMA]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Service Areas', url: '/services' },
          { name: 'Katy, TX', url: CANONICAL_PATH },
        ]}
      />

      {/* Hero */}
      <section className="py-16 md:py-20 bg-[#004C97]" data-testid="katy-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-sm text-white/80 mb-6" data-testid="breadcrumbs">
            <ol className="flex flex-wrap items-center gap-1">
              <li><Link to="/" className="hover:text-[#F9A825]">Home</Link></li>
              <li className="text-white/50">/</li>
              <li><Link to="/services" className="hover:text-[#F9A825]">Service Areas</Link></li>
              <li className="text-white/50">/</li>
              <li className="text-[#F9A825] font-semibold">Katy, TX</li>
            </ol>
          </nav>
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-[#F9A825] text-[#004C97] px-4 py-1.5 rounded-full text-sm font-bold mb-6">
              <MapPin className="w-4 h-4" />
              KATY · FORT BEND · HARRIS · WALLER, TX
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Medical Transportation in Katy, Texas
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              MoveraCare Mobility is based in Richmond, TX and provides professional wheelchair and ambulatory non-emergency medical transportation — including hospital discharge, dialysis, therapy, and Workers&apos; Compensation trips — for Katy patients, families, healthcare facilities, and case managers across the Katy area&apos;s three-county service footprint of Fort Bend, Harris, and Waller.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <Link to="/book">
                <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8 shadow-lg" data-testid="katy-cta-book">
                  Book a Ride
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/request-patient-transportation">
                <Button size="lg" className="bg-white text-[#004C97] hover:bg-gray-100 font-semibold text-lg px-8 shadow-lg" data-testid="katy-cta-refer">
                  Refer a Patient
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:+18329252128">
                <Button size="lg" variant="outline" className="border-2 border-[#F9A825] text-[#F9A825] hover:bg-[#F9A825] hover:text-[#004C97] font-semibold text-lg px-8" data-testid="katy-cta-call">
                  <Phone className="mr-2 w-5 h-5" />
                  Call Dispatch (832) 925-2128
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Katy NEMT Overview */}
      <section className="py-14 bg-white" data-testid="katy-overview">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">KATY NEMT OVERVIEW</p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-6 leading-tight">
            Wheelchair &amp; Ambulatory NEMT for Katy&apos;s Growing Community
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p>
              Few communities in the Greater Houston area have grown as quickly as Katy. What was once a small farming town along the Katy Prairie is now a sprawling collection of master-planned communities spanning three counties — Fort Bend, Harris, and Waller — connected by the Katy Freeway (I-10) and Grand Parkway (99). That geography is unique among Houston-area suburbs, and it shapes how healthcare transportation works here.
            </p>
            <p>
              Katy patients often live in one county and see a physician in another. A Cinco Ranch resident on standing dialysis, a Cross Creek Ranch retiree scheduled for orthopedic follow-up, a Cane Island homeowner recovering from surgery, a Firethorne family arranging discharge for an elderly parent — these are the kinds of trips MoveraCare Mobility is built to handle. Fort Bend County is our core operating area, which places most of the Katy service footprint inside our regular coverage.
            </p>
            <p>
              Services include wheelchair transportation using wheelchair-accessible vehicles, comfortable ambulatory transport, recurring dialysis and therapy transportation, hospital and SNF discharge coordination, and authorized Workers&apos; Compensation transportation. All services are provided for medically stable passengers and are scheduled subject to availability. Extended-distance assignments toward far West Katy or Waller County may require advance authorization.
            </p>
            <p>
              MoveraCare is a non-emergency medical transportation provider. We do not provide ambulance, 911, stretcher ambulance, oxygen therapy, or clinical monitoring services. For medical emergencies, call 911.
            </p>
          </div>
        </div>
      </section>

      {/* Services offered */}
      <section className="py-14 bg-gray-50" data-testid="katy-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">SERVICES OFFERED IN KATY</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] leading-tight">
              Katy Non-Emergency Medical Transportation Services
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICE_TYPES.map((s) => {
              const Icon = s.icon;
              return (
                <Link key={s.title} to={s.to} className="group" data-testid={`katy-service-${s.title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}>
                  <Card className="h-full border-2 hover:border-[#F9A825] transition-all">
                    <CardHeader>
                      <div className="w-11 h-11 rounded-lg bg-[#F9A825] flex items-center justify-center mb-2">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <CardTitle className="text-[#004C97] text-base leading-tight">{s.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 leading-relaxed mb-2">{s.desc}</p>
                      <span className="text-sm font-semibold text-[#F9A825] group-hover:text-[#E08E1E]">Explore →</span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Wheelchair & Ambulatory */}
      <section className="py-14 bg-white" data-testid="katy-wheelchair-ambulatory">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHEELCHAIR TRANSPORTATION</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] mb-4 leading-tight">
                Katy Wheelchair-Accessible Transportation
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Katy&apos;s master-planned communities include a mix of single-family homes, senior-living communities, and assisted-living residences where wheelchair-accessible transportation matters every week. MoveraCare operates wheelchair-accessible vehicles with proper wheelchair securement, and drivers assist passengers during transport. Pickup logistics — from a driveway in Grand Lakes to a covered entry at a Cinco Ranch assisted-living community — are coordinated with family or facility staff at booking. Please share the wheelchair type, weight, and any equipment needs (including oxygen) so dispatch can confirm we can safely serve the trip.
              </p>
              <Link to="/services/wheelchair-transportation" className="inline-flex items-center text-sm font-semibold text-[#F9A825] hover:text-[#E08E1E]">
                Learn about Wheelchair Transportation <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
            <div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">AMBULATORY TRANSPORTATION</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] mb-4 leading-tight">
                Katy Ambulatory Medical Transportation
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                For Katy patients who can walk to and from the vehicle independently or with limited assistance, ambulatory transportation is often the right fit. MoveraCare provides comfortable, professionally driven ambulatory transport for Katy medical, dental, therapy, and specialty appointments — one-way, round-trip, and wait-and-return options are supported when arranged in advance.
              </p>
              <Link to="/services/ambulatory-transportation" className="inline-flex items-center text-sm font-semibold text-[#F9A825] hover:text-[#E08E1E]">
                Learn about Ambulatory Transportation <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Three-county coverage — Katy-specific angle */}
      <section className="py-14 bg-gray-50" data-testid="katy-three-county">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <div className="w-14 h-14 rounded-lg bg-[#F9A825] flex items-center justify-center mb-3">
                <Route className="w-7 h-7 text-white" />
              </div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">THREE-COUNTY KATY</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] leading-tight">
                Katy Doesn&apos;t Sit In One County — Neither Does Our Coverage
              </h2>
            </div>
            <div className="lg:col-span-2">
              <p className="text-gray-700 leading-relaxed mb-4">
                A Katy address can be in Fort Bend County, Harris County, or Waller County depending on the neighborhood. That matters for medical transportation because appointment destinations, insurer networks, discharge destinations, and Workers&apos; Compensation authorized providers may sit in a different county than where the patient lives.
              </p>
              <p className="text-gray-700 leading-relaxed">
                MoveraCare&apos;s core operating area is Fort Bend County, and the majority of the Katy service footprint sits in or immediately next to that coverage. Harris County trips within our service area are supported. Trips reaching into far West Katy or Waller County can typically be accommodated with advance scheduling and, in some cases, extended-distance authorization for longer runs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* For Katy Healthcare Organizations */}
      <section className="py-14 bg-[#004C97]" data-testid="katy-healthcare-org">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">FOR KATY HEALTHCARE ORGANIZATIONS</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
              Transportation for Katy Hospitals, SNFs, Dialysis Centers &amp; Case Managers
            </h2>
            <p className="text-gray-200 mt-3 max-w-3xl">
              MoveraCare works directly with Katy-area hospitals, skilled nursing facilities, rehabilitation centers, dialysis centers, case managers, discharge planners, and social workers to coordinate reliable non-emergency medical transportation.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {NEIGHBORHOOD_ANCHORS.map((a) => (
              <div key={a.name} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
                <h3 className="text-white font-semibold text-lg mb-2">{a.name}</h3>
                <p className="text-gray-200 text-sm leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to="/healthcare-partners">
              <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold" data-testid="katy-cta-healthcare-partners">
                Healthcare Partner Programs
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/healthcare-partners/hospital-discharge">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#004C97] font-semibold">
                Hospital Discharge
              </Button>
            </Link>
            <Link to="/healthcare-partners/dialysis-centers">
              <Button size="lg" variant="outline" className="border-2 border-[#F9A825] text-[#F9A825] hover:bg-[#F9A825] hover:text-[#004C97] font-semibold">
                Dialysis Centers
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Workers' Compensation for Katy */}
      <section className="py-14 bg-white" data-testid="katy-workers-comp">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div>
              <div className="w-14 h-14 rounded-lg bg-[#F9A825] flex items-center justify-center mb-3">
                <Briefcase className="w-7 h-7 text-white" />
              </div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WORKERS&apos; COMPENSATION</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] leading-tight">
                Katy Workers&apos; Compensation Transportation
              </h2>
            </div>
            <div className="lg:col-span-2">
              <p className="text-gray-700 leading-relaxed mb-4">
                MoveraCare provides non-emergency transportation for Katy-area injured workers attending Workers&apos; Compensation-authorized medical, therapy, IME, FCE, occupational medicine, and diagnostic appointments — often across county lines when the authorized provider isn&apos;t in the same county as the worker. We coordinate with adjusters, nurse case managers, TPAs, and ancillary service coordinators. Authorization determinations are made by the applicable payer, adjuster, case manager, or TPA — being a network vendor does not automatically guarantee that a trip is covered or payable.
              </p>
              <Link to="/healthcare-partners/workers-compensation" className="inline-flex items-center text-sm font-semibold text-[#F9A825] hover:text-[#E08E1E]" data-testid="katy-cta-workers-comp">
                Workers&apos; Compensation Transportation Details <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-14 bg-gray-50" data-testid="katy-areas-served">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">AREAS SERVED</p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4 leading-tight">
            Katy Neighborhoods, Master-Planned Communities &amp; Nearby Coverage
          </h2>
          <p className="text-gray-700 mb-6 max-w-3xl">
            The Katy service footprint is anchored in Fort Bend County (MoveraCare&apos;s core operating area) and extends into Harris and Waller Counties. Service is scheduled subject to availability. Extended-distance assignments may require advance authorization.
          </p>
          <div className="flex flex-wrap gap-2">
            {KATY_AREAS.map((a) => (
              <span key={a} className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-[#004C97] font-medium">
                {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose MoveraCare */}
      <section className="py-14 bg-white" data-testid="katy-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHY CHOOSE MOVERACARE</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97]">Built for Katy Healthcare Transportation</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: MapPin, title: 'Three-County Katy Coverage', desc: 'Fort Bend core coverage plus Harris County support and Waller County extended-distance scheduling.' },
              { icon: Accessibility, title: 'Wheelchair-Accessible Vehicles', desc: 'Wheelchair-accessible vehicles with proper wheelchair securement. Drivers assist passengers during transport.' },
              { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport. Mobility and equipment needs are confirmed at booking.' },
              { icon: Repeat, title: 'Standing Schedules', desc: 'Reliable recurring transportation for Katy dialysis, therapy, and treatment programs.' },
              { icon: Hospital, title: 'Discharge-Ready Dispatch', desc: 'Responsive same-day and scheduled Katy hospital and SNF discharge coordination.' },
              { icon: Building2, title: 'Credentialing-Ready', desc: 'Licensing, insurance, and vendor documentation available for Katy facility procurement teams.' },
            ].map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.title} className="flex items-start gap-3 p-5 border-2 border-gray-100 rounded-xl hover:border-[#F9A825] transition-all">
                  <div className="w-10 h-10 rounded-lg bg-[#F9A825]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#F9A825]" />
                  </div>
                  <div>
                    <h3 className="text-[#004C97] font-semibold mb-1 leading-tight">{w.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-gray-50" data-testid="katy-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">KATY FAQ</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97]">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {KATY_FAQS.map((f) => (
              <details key={f.question} className="bg-white border-2 border-gray-100 rounded-lg p-5 group">
                <summary className="font-semibold text-[#004C97] cursor-pointer flex items-center justify-between">
                  <span>{f.question}</span>
                  <span className="text-[#F9A825] transition-transform group-open:rotate-90">→</span>
                </summary>
                <p className="mt-3 text-sm text-gray-700 leading-relaxed">{f.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 bg-[#F9A825]" data-testid="katy-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">
            Request Katy Medical Transportation
          </h2>
          <p className="text-lg text-[#004C97]/85 mb-8">
            Book a Katy wheelchair or ambulatory ride, refer a patient, or reach dispatch directly.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
            <Link to="/book">
              <Button size="lg" className="bg-[#004C97] text-white hover:bg-[#003D7A] font-semibold text-lg px-8 py-6 shadow-lg" data-testid="katy-final-book">
                Book a Ride
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/request-patient-transportation">
              <Button size="lg" className="bg-white text-[#004C97] hover:bg-gray-100 font-semibold text-lg px-8 py-6 shadow-lg" data-testid="katy-final-refer">
                Refer a Patient
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+18329252128">
              <Button size="lg" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold text-lg px-8 py-6" data-testid="katy-final-call">
                <Phone className="mr-2 w-5 h-5" />
                (832) 925-2128
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Non-emergency disclaimer */}
      <section className="py-6 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-100 border-l-4 border-gray-400 rounded p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700 leading-relaxed">
              MoveraCare provides <strong>non-emergency medical transportation</strong> for medically stable passengers in Katy and the surrounding Fort Bend, Harris, and Waller County areas. We do not provide ambulance, 911, emergency response, stretcher ambulance, oxygen therapy, or clinical monitoring services. <strong>Not for medical emergencies. Call 911 in an emergency.</strong>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KatyMedicalTransportation;
